import { useTextInputAutoResize } from '@euler/functions/useTextInputAutoResize';
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  NativeSyntheticEvent,
  TextInput,
  TextInputContentSizeChangeEventData,
  TextInputProps,
} from 'react-native';
import Animated from 'react-native-reanimated';

const AnimatedTextInput = Animated.createAnimatedComponent(TextInput);

export type AutoResizeTextInputRef = {
  focus: () => void;
};

type Props = TextInputProps & {
  padding?: number;
  lineHeight: number;
  minNumberOfLines: number;
  maxNumberOfLines: number;
};

export const AutoResizeTextInput = memo(
  forwardRef<AutoResizeTextInputRef, Props>(
    (
      {
        lineHeight,
        padding = 0,
        minNumberOfLines,
        maxNumberOfLines,
        style,
        onContentSizeChange,
        ...props
      },
      ref,
    ) => {
      const textInput = useRef<TextInput>(null);
      useImperativeHandle(ref, () => ({
        focus: () => textInput.current?.focus(),
      }));
      const {
        animatedStyle,
        onContentSizeChange: onAutoResizeContentSizeChange,
      } = useTextInputAutoResize({
        minHeight: minNumberOfLines * lineHeight + 2 * padding,
        maxHeight: maxNumberOfLines * lineHeight + 2 * padding,
        padding,
      });
      const onInternalContentSizeChange = useCallback(
        (e: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
          onAutoResizeContentSizeChange(e);
          onContentSizeChange?.(e);
        },
        [onAutoResizeContentSizeChange, onContentSizeChange],
      );
      return (
        <AnimatedTextInput
          ref={textInput}
          onContentSizeChange={onInternalContentSizeChange}
          style={[style, animatedStyle]}
          {...props}
        />
      );
    },
  ),
);
